<template>
    <div class="edit-content" v-if="activeClient">
        <!-- Id (readonly) -->
        <div class="edit-row" v-if="activeClient.id">
            <span class="row-title">Id</span>
            <span class="row-value-readonly">{{ activeClient.id }}</span>
        </div>

        <!-- Name, Chain name, Shop name -->
        <div class="edit-row">
            <span class="row-title">{{ "Name" | t }} <span class="required">*</span></span>
            <v-text-field
                :value="activeClient.name"
                class="compact-input"
                dense
                hide-details
                outlined
                @input="updateField($event, 'name')"
            />
        </div>
        <div class="edit-row">
            <span class="row-title">{{ "Chain name" | t }} <span class="required">*</span></span>
            <v-text-field
                :value="activeClient.chain_name"
                class="compact-input"
                dense
                hide-details
                outlined
                @input="updateField($event, 'chain_name')"
            />
        </div>
        <div class="edit-row">
            <span class="row-title">{{ "Shop name" | t }} <span class="required">*</span></span>
            <v-text-field
                :value="activeClient.shop_name"
                class="compact-input"
                dense
                hide-details
                outlined
                @input="updateField($event, 'shop_name')"
            />
        </div>

        <!-- Business type, Shop type -->
        <div class="edit-row">
            <span class="row-title">{{ "Business type" | t }}</span>
            <v-text-field
                :value="activeClient.business_type"
                class="compact-input"
                dense
                hide-details
                outlined
                @input="updateField($event, 'business_type')"
            />
        </div>
        <div class="edit-row">
            <span class="row-title">{{ "Shop type" | t }}</span>
            <v-text-field
                :value="activeClient.shop_type"
                class="compact-input"
                dense
                hide-details
                outlined
                @input="updateField($event, 'shop_type')"
            />
        </div>

        <!-- Comment, Address fields -->
        <div class="edit-row">
            <span class="row-title">{{ "Comment" | t }}</span>
            <v-text-field
                :value="activeClient.comment"
                class="compact-input"
                dense
                hide-details
                outlined
                @input="updateField($event, 'comment')"
            />
        </div>
        <div class="edit-row">
            <span class="row-title">{{ "Shop address" | t }}</span>
            <v-text-field
                :value="activeClient.shop_address"
                class="compact-input"
                dense
                hide-details
                outlined
                @input="updateField($event, 'shop_address')"
            />
        </div>
        <div class="edit-row">
            <span class="row-title">{{ "Shop place" | t }}</span>
            <v-text-field
                :value="activeClient.shop_place"
                class="compact-input"
                dense
                hide-details
                outlined
                @input="updateField($event, 'shop_place')"
            />
        </div>
        <div class="edit-row">
            <span class="row-title">{{ "Shop parish" | t }}</span>
            <v-text-field
                :value="activeClient.shop_parish"
                class="compact-input"
                dense
                hide-details
                outlined
                @input="updateField($event, 'shop_parish')"
            />
        </div>
        <div class="edit-row">
            <span class="row-title">{{ "Shop county" | t }}</span>
            <v-text-field
                :value="activeClient.shop_county"
                class="compact-input"
                dense
                hide-details
                outlined
                @input="updateField($event, 'shop_county')"
            />
        </div>
        <div class="edit-row">
            <span class="row-title">{{ "Shop index" | t }}</span>
            <v-text-field
                :value="activeClient.shop_index"
                class="compact-input"
                dense
                hide-details
                outlined
                @input="updateField($event, 'shop_index')"
            />
        </div>
        <div class="edit-row">
            <span class="row-title">{{ "Shop country" | t }}</span>
            <v-text-field
                :value="activeClient.shop_country"
                class="compact-input"
                dense
                hide-details
                outlined
                @input="updateField($event, 'shop_country')"
            />
        </div>
        <div class="edit-row">
            <span class="row-title">{{ "Shop country code" | t }}</span>
            <v-autocomplete
                v-model="shopCountry"
                :items="countries"
                class="compact-input"
                dense
                hide-details
                hide-no-data
                item-text="display_name"
                item-value="code"
                outlined
                :placeholder="t('Start typing to Search')"
                return-object
                clearable
            />
        </div>

        <!-- Latitude, Longitude -->
        <div class="edit-row">
            <span class="row-title">{{ "Latitude" | t }}</span>
            <v-text-field
                :value="activeClient.latitude"
                class="compact-input"
                dense
                hide-details
                outlined
                type="number"
                @input="updateField($event, 'latitude')"
            />
        </div>
        <div class="edit-row">
            <span class="row-title">{{ "Longitude" | t }}</span>
            <v-text-field
                :value="activeClient.longitude"
                class="compact-input"
                dense
                hide-details
                outlined
                type="number"
                @input="updateField($event, 'longitude')"
            />
        </div>

        <!-- Client country, Price list, Contractual relationship -->
        <div class="edit-row">
            <span class="row-title">{{ "Client country" | t }} <span class="required">*</span></span>
            <v-autocomplete
                v-model="country"
                :items="countries"
                :loading="isLoading"
                class="compact-input"
                dense
                hide-details
                hide-no-data
                item-text="display_name"
                item-value="code"
                outlined
                :placeholder="t('Start typing to Search')"
                return-object
            />
        </div>
        <div class="edit-row">
            <span class="row-title">{{ "Price list" | t }}</span>
            <v-text-field
                :value="activeClient.price_list"
                class="compact-input"
                dense
                hide-details
                outlined
                @input="updateField($event, 'price_list')"
            />
        </div>
        <div class="edit-row">
            <span class="row-title">{{ "Contractual relationship" | t }}</span>
            <v-text-field
                :value="activeClient.contractual_relationship"
                class="compact-input"
                dense
                hide-details
                outlined
                @input="updateField($event, 'contractual_relationship')"
            />
        </div>

        <!-- Tags -->
        <div class="edit-row">
            <span class="row-title">{{ "Tags" | t }}</span>
            <v-autocomplete
                v-model="selectedTagIds"
                :items="tags"
                item-text="name"
                item-value="id"
                multiple
                chips
                small-chips
                deletable-chips
                dense
                outlined
                hide-details
                class="compact-input"
                :placeholder="t('Select tags')"
            />
        </div>
    </div>
</template>

<script>
import Store from "@/store";
import { mapState } from "vuex";

export default {
    name: "client-info-edit-tab",
    data() {
        return {
            country: {},
            shopCountry: null,
            isLoading: true
        };
    },
    async mounted() {
        if (this.countries.length === 0) {
            await Store.dispatch("fetchCountries");
        }

        setTimeout(() => {
            if (this.activeClient && this.activeClient.country_code) {
                this.country = this.countries.find(x => x.code === this.activeClient.country_code);
            }
            if (this.activeClient && this.activeClient.shop_country_code) {
                this.shopCountry = this.countries.find(x => x.code === this.activeClient.shop_country_code);
            }
            this.isLoading = false;
        }, 200);
    },
    watch: {
        country: function(value) {
            value && value.code
                ? this.updateField(value.code, "country")
                : this.updateField(null, "country");
        },
        shopCountry: function(value) {
            value && value.code
                ? this.updateField(value.code, "shop_country_code")
                : this.updateField(null, "shop_country_code");
        }
    },
    computed: {
        ...mapState(["tags", "countries"]),
        ...mapState("ClientStore", ["activeClient"]),
        selectedTagIds: {
            get() {
                return (this.activeClient.tags || []).map(t => t.id);
            },
            set(val) {
                this.updateField(val, "tag_ids");
                const selectedTags = (this.tags || []).filter(t => val.includes(t.id));
                this.updateField(selectedTags, "tags");
            }
        }
    },
    methods: {
        updateField(value, field) {
            Store.commit("ClientStore/updateActiveClientProperty", { prop: field, value: value });
        }
    }
};
</script>

<style scoped>
.edit-content {
    padding: 12px 20px;
}

.edit-row {
    display: flex;
    align-items: center;
    min-height: 32px;
    margin-bottom: 4px;
}

.row-title {
    color: #888;
    width: 140px;
    flex-shrink: 0;
    font-size: 13px;
}

.row-value-readonly {
    font-size: 13px;
    color: #333;
}

.required {
    color: #f41823;
}

.compact-input {
    flex: 1;
    max-width: 300px;
}

::v-deep .compact-input .v-input__slot {
    min-height: 28px !important;
    padding: 0 8px !important;
}

::v-deep .compact-input input {
    padding: 4px 0 !important;
    font-size: 13px;
}

::v-deep .compact-input .v-select__selections {
    padding: 0 !important;
}

::v-deep .compact-input .v-input__prepend-inner,
::v-deep .compact-input .v-input__append-inner {
    margin-top: 4px !important;
}
</style>
